import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './style1.css';

interface DemosProps {
    username?: string | null;
    onLogout?: () => void;
}

interface Demo {
    id: number;
    name: string;
    description: string;
    videoUrl: string;
}

const navLinks = [
    { name: 'HOME', href: '/' },
    { name: 'PROGRAMS', href: '/programs' },
    { name: 'VIDEOS', href: '/videos' },
    { name: 'IMAGES', href: '/images' },
    { name: 'DEMOS', href: '/demos' },
    { name: 'CERTIFICATES', href: '/certificates' },
    { name: 'ABOUT', href: '/about' },
];

function renderDescription(desc: string) {
    // Very simple, predictable parser:
    // - Splits on whitespace
    // - Treats tokens starting with "/" as internal links
    // - Treats https? URLs and bare domains (with optional path) as external https links
    const lines = desc.split('\n');
    const parts = lines.map((line, idx) => {
        const segments = line.split(/(\s+)/).map((token, i) => {
            // Keep whitespace tokens as-is
            if (/^\s+$/.test(token) || token === '') {
                return token;
            }

            // Separate trailing punctuation so "comput3.cloud," still links correctly
            const match = token.match(/^(.+?)([.,!?;:)]*)$/);
            const core = match ? match[1] : token;
            const trailing = match ? match[2] : '';

            // Internal route like "/demos" or "/programs/foo"
            if (core.startsWith('/')) {
                return (
                    <React.Fragment key={i}>
                        <Link
                            to={core}
                            style={{ color: '#2575fc', textDecoration: 'underline', wordBreak: 'break-all' }}
                        >
                            {core}
                        </Link>
                        {trailing}
                    </React.Fragment>
                );
            }

            const isHttp = /^https?:\/\//i.test(core);
            const isDomain = !isHttp && /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}([\/\?#].*)?$/i.test(core);

            if (isHttp || isDomain) {
                let href = core;
                // Force HTTPS for all external links
                if (!/^https:\/\//i.test(href)) {
                    if (/^http:\/\//i.test(href)) {
                            // upgrade http -> https
                            href = href.replace(/^http:\/\//i, 'https://');
                        } else {
                            href = 'https://' + href;
                        }
                }
                return (
                    <React.Fragment key={i}>
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#2575fc', textDecoration: 'underline', wordBreak: 'break-all' }}
                        >
                            {core}
                        </a>
                        {trailing}
                    </React.Fragment>
                );
            }

            // Not a link – return raw token
            return token;
        });

        return (
            <React.Fragment key={idx}>
                {segments}
                {idx < lines.length - 1 ? <br /> : null}
            </React.Fragment>
        );
    });
    return <span>{parts}</span>;
}

// Use environment variable for API base URL in production
const API_BASE =
    process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_API_BASE || 'https://timlweb.com')
        : '';

const Demos: React.FC<DemosProps> = ({ username, onLogout }) => {
    const [demos, setDemos] = useState<Demo[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token');
        fetch(`${API_BASE}/alldemos`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
            .then(res => res.json())
            .then(data => {
                // Show most recently added demos first by numeric id (backend returns { id: number, ... }).
                if (Array.isArray(data)) {
                    const sorted = data.slice().sort((a: any, b: any) => {
                        const idA = typeof a.id === 'number' ? a.id : parseInt(a.id, 10) || 0;
                        const idB = typeof b.id === 'number' ? b.id : parseInt(b.id, 10) || 0;
                        return idB - idA;
                    });
                    setDemos(sorted);
                } else {
                    setDemos([]);
                }
            })
            .catch(() => setDemos([]))
            .finally(() => setLoading(false));
    }, []);

    const [navOpen, setNavOpen] = useState(false);

    return (
        <div className="modern-layout">
            <header className="modern-header">
                <div className="header-container">
                    <button
                        className="menu-toggle"
                        aria-label={navOpen ? 'Close menu' : 'Open menu'}
                        onClick={() => setNavOpen(v => !v)}
                    >
                        {/* Burger icon */}
                        <span style={{ display: 'inline-block', width: 28, height: 28 }}>
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect y="5" width="28" height="3" rx="1.5" fill="#2575fc" />
                                <rect y="12.5" width="28" height="3" rx="1.5" fill="#2575fc" />
                                <rect y="20" width="28" height="3" rx="1.5" fill="#2575fc" />
                            </svg>
                        </span>
                    </button>
                    <nav className={`modern-nav${navOpen ? ' open' : ''}`} onClick={() => setNavOpen(false)}>
                        {navLinks.map(link =>
                            link.href.startsWith('/')
                                ? <Link
                                    key={link.name}
                                    to={link.href}
                                    className="modern-nav-link"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {link.name}
                                  </Link>
                                : <a
                                    key={link.name}
                                    href={link.href}
                                    className="modern-nav-link"
                                  >
                                    {link.name}
                                  </a>
                        )}
                        {onLogout && (
                            <button className="logout-btn" onClick={onLogout}>LOG OUT</button>
                        )}
                    </nav>
                </div>
            </header>
            <main className="modern-content">
                <h2 style={{ textAlign: 'center', color: '#2575fc', marginBottom: '2rem' }}>Demos</h2>
                {loading ? (
                    <div style={{ textAlign: 'center', color: '#888' }}>Loading...</div>
                ) : demos.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#888' }}>No demos found.</div>
                ) : (
                    demos.map(demo => (
                        <div
                            key={demo.id}
                            style={{
                                background: 'transparent',
                                padding: '2rem',
                                maxWidth: 600,
                                margin: '2rem auto'
                            }}
                        >
                            <h3 style={{ color: '#000', marginBottom: '1.2rem', textAlign: 'center' }}>
                                {demo.name}
                            </h3>
                            <div style={{ marginBottom: '1.5rem', fontSize: '1.08rem', color: '#000', whiteSpace: 'pre-line' }}>
                                {renderDescription(demo.description)}
                            </div>
                            {demo.videoUrl && (
                                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                                    <video
                                        controls
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        style={{
                                            width: '100%',
                                            maxWidth: 520,
                                            borderRadius: '12px',
                                            background: '#000'
                                        }}
                                    >
                                        <source src={`${API_BASE}${demo.videoUrl}`} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </main>
            <footer className="modern-footer">
                &copy; {new Date().getFullYear()} Timo
            </footer>
        </div>
    );
};

export default Demos;